// Editor for the five "hard-coded" calculation inputs that feed the calc
// engine: subscriptions, interest_distributions, outstanding_bids,
// misc_bank_cost, issue_dates. All five live in the same PreflightSettings
// document (GET/PUT /compartment-report/settings, which replaces the WHOLE
// document - no partial patch, see preflight.py's PreflightSettings model),
// so this dialog fetches the current settings, lets the user edit a local
// draft across 5 tabs, and on "Confirm & Re-Calculate" PUTs the full draft
// back and then immediately runs a `calculate` job for `reportingId` - ports
// reporting-hub's HardcodedValuesDialog.tsx, notably keeping its "no plain
// Save" behavior: saving without recalculating would leave stale numbers in
// the Calculation Results view, so there is deliberately only the one
// combined action.
//
// Simplifications vs. the original (see OutstandingBidsForm.js's own note):
// the read-only "+ From Pending Loans" / "+ From Manual Exclusions" /
// "= Effective Total" breakdown columns on the Outstanding Bids tab aren't
// reproduced here, and there's no ConfirmInvalidateDialog step warning that
// an existing Report will go stale - Confirm & Re-Calculate always proceeds
// directly. Flagged in the handback report for Nimesh to decide whether to
// add later.

import React, { useEffect, useState } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import SubscriptionsTable from './SubscriptionsTable';
import InterestDistributionsTable from './InterestDistributionsTable';
import OutstandingBidsForm from './OutstandingBidsForm';
import IssueDatesForm from './IssueDatesForm';
import JobProgress from '../JobProgress';
import LoadingState from '../../common/LoadingState';
import { apiErrorDetail, useWizardJob } from '../jobs';
import errorMessage from '../../../helpers/errorMessage';

const HardcodedValuesDialog = ({ reportingId, visible, onHide, onSaved, setLoading }) => {
  const [draft, setDraft] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const { runJob, activeJob, busy } = useWizardJob();

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setFetchError(null);
    setDraft(null);
    axiosWithAuth
      .get('/compartment-report/settings')
      .then((res) => {
        if (!cancelled) setDraft(res.data);
      })
      .catch((err) => {
        console.error('[HardcodedValuesDialog] fetch settings error:', errorMessage(err));
        if (!cancelled) setFetchError(errorMessage(err, 'Failed to load current settings.'));
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const handleConfirm = async () => {
    if (!draft) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await axiosWithAuth.put('/compartment-report/settings', draft);
      await runJob(reportingId, 'calculate', {});
      setIsSaving(false);
      onSaved && onSaved();
      onHide();
      return;
    } catch (err) {
      if (err.cancelled) return;
      console.error('[HardcodedValuesDialog] save/recalculate error:', err.detail || errorMessage(err));
      setSaveError(apiErrorDetail(err, 'Failed to save values and recalculate.'));
    }
    setIsSaving(false);
  };

  const handleHide = () => {
    if (isSaving) return;
    onHide();
  };

  return (
    <Dialog
      header="Edit Hard-Coded Values"
      visible={visible}
      onHide={handleHide}
      style={{ width: '55rem', maxWidth: '95vw' }}
      maximizable
      closable={!isSaving}
    >
      {fetchError && <div className="ex-inline-error mb-3">{fetchError}</div>}
      {saveError && <div className="ex-inline-error mb-3">{saveError}</div>}

      {!draft ? (
        !fetchError && <LoadingState label="Loading settings" />
      ) : (
        <TabView>
          <TabPanel header="Subscriptions">
            <p className="cr-help-text mt-0">
              New subscriptions per Originator and date, used by the "Cost Calculation" step. Add a row for every new
              subscription as it happens - past entries stay valid, new ones can be appended any time.
            </p>
            <SubscriptionsTable
              entries={draft.subscriptions || []}
              onChange={(entries) => setDraft({ ...draft, subscriptions: entries })}
            />
          </TabPanel>

          <TabPanel header="Interest Distributions">
            <p className="cr-help-text mt-0">
              Total interest distributed to investors, by date, used by the "Interest Overview" step. Add a row for
              every distribution as it happens - past entries stay valid, new ones can be appended any time.
            </p>
            <InterestDistributionsTable
              entries={draft.interest_distributions || []}
              onChange={(entries) => setDraft({ ...draft, interest_distributions: entries })}
            />
          </TabPanel>

          <TabPanel header="Outstanding Bids">
            <p className="cr-help-text mt-0">
              Current outstanding bids per Originator, used by the Originator-level "General Overview" NAV calculation.
              Defaults to 0 until set.
            </p>
            <OutstandingBidsForm
              values={draft.outstanding_bids || {}}
              onChange={(values) => setDraft({ ...draft, outstanding_bids: values })}
            />
          </TabPanel>

          <TabPanel header="Misc Bank Cost">
            <p className="cr-help-text mt-0">
              Current miscellaneous bank costs per Originator, used by the "NAV Calculation" step (NAV Flows method).
              Defaults to 0 until set.
            </p>
            <OutstandingBidsForm
              values={draft.misc_bank_cost || {}}
              columnLabel="Misc Bank Cost"
              onChange={(values) => setDraft({ ...draft, misc_bank_cost: values })}
            />
          </TabPanel>

          <TabPanel header="Issue Dates">
            <p className="cr-help-text mt-0">
              Note issue date per Originator, shown on that Originator's Report factsheet ("Issue Date"). Not derived from
              LoanTape/CashFlows - a plain fact only a human knows.
            </p>
            <IssueDatesForm
              values={draft.issue_dates || {}}
              onChange={(values) => setDraft({ ...draft, issue_dates: values })}
            />
          </TabPanel>
        </TabView>
      )}

      {isSaving && activeJob && activeJob.action === 'calculate' && <JobProgress job={activeJob} style={{ marginTop: '1rem' }} />}

      <div className="cr-dialog-actions mt-4">
        <Button label="Cancel" className="p-button-outlined" onClick={handleHide} disabled={isSaving} />
        <Button
          label={isSaving ? 'Saving...' : 'Confirm & Re-Calculate'}
          icon={isSaving ? 'pi pi-spin pi-spinner' : undefined}
          onClick={handleConfirm}
          disabled={!draft || isSaving || busy}
        />
      </div>
    </Dialog>
  );
};

export default HardcodedValuesDialog;
